import { appZone, dayInZone } from '@/core/time';
import { peekWorldCache } from '@/modules/activity/trace';
import { getDayZones, peekTrips } from '@/modules/connections/trips';
import { tracePoints, tripsZoneAt, type ZonePoint } from './zones';

// Where (which zone) the user was at a moment, in the same order Sleep uses: the day you said,
// then Trace, then Trips App. For filing a weigh-in or a meal under the right day when it was
// logged while travelling, or imported without a zone.

let cached: { cache: unknown; points: ZonePoint[] } | null = null;

function points(): ZonePoint[] {
  const cache = peekWorldCache();
  if (!cached || cached.cache !== cache) cached = { cache, points: tracePoints(cache) };
  return cached.points;
}

const NEAR_MS = 36 * 3_600_000;
const NOW_MS = 12 * 3_600_000;

/** Where Trace (seen in the last 12 hours) or else Trips App (a stay or trip now) has you. */
export function sensedZone(ms = Date.now()): { tz: string; from: 'Trace' | 'Trips App' } | null {
  const p = points();
  for (let i = p.length - 1; i >= 0; i--) {
    const c = p[i]!;
    if (c.at > ms) continue;
    if (ms - c.at <= NOW_MS) return { tz: c.tz, from: 'Trace' };
    break;
  }
  const trip = tripsZoneAt(peekTrips(), ms, NOW_MS);
  return trip ? { tz: trip, from: 'Trips App' } : null;
}

/** A day you said where you were, if `ms` falls on it there. */
function toldAt(ms: number): string | undefined {
  for (const [day, tz] of Object.entries(getDayZones())) {
    try {
      if (dayInZone(ms, tz) === day) return tz;
    } catch {}
  }
  return undefined;
}

/** The zone closest to `ms` (within a day and a half) from what you said, Trace, Trips App; else the app's zone. */
export function zoneAt(ms: number): string {
  const told = toldAt(ms);
  if (told) return told;
  const p = points();
  let best: ZonePoint | undefined;
  let bestD = NEAR_MS;
  // Points are sorted; a binary search finds the neighbours.
  let lo = 0;
  let hi = p.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (p[mid]!.at <= ms) lo = mid + 1;
    else hi = mid;
  }
  for (const c of [p[lo - 1], p[lo]]) {
    if (c && Math.abs(c.at - ms) < bestD) {
      best = c;
      bestD = Math.abs(c.at - ms);
    }
  }
  return best?.tz ?? tripsZoneAt(peekTrips(), ms, NEAR_MS) ?? appZone();
}
